'use client';

import { Button } from '@/components/button';
import HeadlineField from '@/components/customFields/HeadlineField';
import { StoryblokImage } from '@/components/customFields/StoryblokImage';
import { HeadlineTag } from '@/components/customFields/types';
import { convertToHeadlineTag } from '@/components/customFields/utils';
import { RichText } from '@/components/richText/RichText';
import { SvgIcon } from '@/components/svgIcon';
import { type ReplacementsType, replacePlaceholders } from '@/helper/replacements';
import tracking, { type PromotionTrackingItem } from '@/helper/tracking';
import { useScrollTrigger } from '@/hooks/useScrollTrigger';
import ArrowRight from '@/public/icons/pfeil_rechts.svg';
import type { ComponentTeaserStoryblok } from '@/types/component-types-sb';
import { ColumnWidth, type ImageWidthDynamic } from '@/types/layoutTypes';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';

import type { TeaserPromotionTracking, TeaserVariant } from './Teaser';
import classes from './Teaser.module.scss';

interface TeaserClientProps {
  blok: ComponentTeaserStoryblok;
  promotionTracking?: TeaserPromotionTracking;
  replace?: ReplacementsType;
  variant?: TeaserVariant;
}

const TeaserClient: React.FC<TeaserClientProps> = ({ blok, promotionTracking, replace, variant }) => {
  const isSearchVariant = variant === 'search';
  const ref = useRef<HTMLDivElement>(null);
  const trackedPromotionView = useRef<string | null>(null);
  /*
    `consentState` represents the user-consent state for animation:
    - null  → consent pending/unknown
    - true  → consent explicitly set (allowed)
    - false → consent explicitly set (not allowed)

    When `blok.animate` is true, we initially keep the teaser hidden (opacity: 0 via `classes.teaserAnimated`) while
    consent is pending. This avoids a flicker where the element would render at opacity: 1 and then jump to 0 before
    animating.

    Class application (see JSX below):
    - `classes.teaserAnimated` is applied when `consentState` is null or true (initial hidden/prepare state).
    - `classes.teaserAnimatedTriggered` is applied when the element is visible and `consentState` is true to start the animation.
    - If `consentState` is false, no animation classes are applied.
  */
  const [consentState, setConsentState] = useState<boolean | null>(null);
  const visible = useScrollTrigger(ref);
  const layout: ImageWidthDynamic = {
    columnsMobile: ColumnWidth.FULL,
    columnsTablet: ColumnWidth.FULL,
    columnsDesktop: ColumnWidth.FULL,
  };
  const classAlignment = blok.alignment ? classes.textRight : '';
  const hasImage = !!blok.image1 && !!blok.image1.filename;
  const creativeName = blok.title?.trim() || blok._uid;
  const isPersonalized = promotionTracking?.isPersonalized;
  let classImageWidth = '';

  const getPromotionTrackingItem = (): PromotionTrackingItem | undefined => {
    if (isPersonalized === undefined) {
      return undefined;
    }

    return {
      promotion_id: blok._uid,
      creative_name: creativeName,
      is_personalized: isPersonalized,
    };
  };

  useEffect(() => {
    if (!visible || isPersonalized === undefined) {
      return;
    }

    const promotionViewKey = `${blok._uid}:${isPersonalized}`;
    if (trackedPromotionView.current === promotionViewKey) {
      return;
    }

    trackedPromotionView.current = promotionViewKey;
    tracking.viewPromotion({
      promotion_id: blok._uid,
      creative_name: creativeName,
      is_personalized: isPersonalized,
    });
  }, [blok._uid, creativeName, isPersonalized, visible]);

  useEffect(() => {
    // The consent state check is only required when no animation is activated
    if (!blok.animate) {
      return;
    }

    const checkConsentSetExplicitly = () => {
      // We don't need this when there is no animation at all.
      if (consentState || !blok.animate) {
        return true;
      }

      if (
        typeof window.UC_UI_IS_RENDERED !== 'undefined' &&
        window.UC_UI_IS_RENDERED &&
        typeof window.UC_UI !== 'undefined' &&
        window.UC_UI
      ) {
        const serviceBaseInfo = window.UC_UI.getServicesBaseInfo();
        let explicitlySet = false;
        for (const service of serviceBaseInfo) {
          for (const consent of service.consent.history) {
            if (consent.type === 'explicit') {
              explicitlySet = true;
              break;
            }
          }
        }

        if (explicitlySet) {
          setConsentState(true);
        } else {
          setConsentState(false);
        }
        return explicitlySet;
      }
      return false;
    };

    // Check regularly if the consent has been set explicitly
    const interval = setInterval(() => {
      if (checkConsentSetExplicitly()) {
        // Stop the interval, as soon the consent has been explicitly set
        clearInterval(interval);
      }
    }, 250); // Adjust the delay time as needed

    const onConsentHandler = () => {
      if (checkConsentSetExplicitly()) {
        // Stop the interval, as soon the consent has been explicitly set
        clearInterval(interval);
      }
    };

    window.addEventListener('onConsentStatusChange', onConsentHandler);
    return () => {
      window.removeEventListener('onConsentStatusChange', onConsentHandler);
      clearInterval(interval);
    };
  }, [blok.animate, consentState]);

  if (hasImage) {
    switch (blok.imageWidth) {
      case 'sm':
        classImageWidth = classes.singleImageSm!;
        break;
      case 'md':
        classImageWidth = classes.singleImageMd!;
        break;
      case 'lg':
        classImageWidth = classes.singleImageLg!;
        break;
      case 'xl':
        classImageWidth = classes.singleImageXl!;
        break;
    }
  }

  let color = 'white';
  if (blok.color?.name?.startsWith('green')) {
    color = 'green';
  }

  const headline = blok.headline && replace ? replacePlaceholders(replace, blok.headline) : blok.headline;
  const ctaLabel = blok.ctaText && replace ? replacePlaceholders(replace, blok.ctaText) : blok.ctaText;
  const handleCtaClick = () => {
    const promotionTrackingItem = getPromotionTrackingItem();
    if (promotionTrackingItem) {
      tracking.selectPromotion(promotionTrackingItem);
    }
  };

  return (
    <div
      ref={ref}
      className={classNames(classes.teaser, classImageWidth, classAlignment, {
        [classes.search]: isSearchVariant,
        [classes.searchWithImage]: isSearchVariant && hasImage,
        [classes.teaserAnimatedTriggered]: !isSearchVariant && blok.animate && visible && consentState,
        [classes.teaserAnimated]: !isSearchVariant && blok.animate && (consentState || consentState === null),
        [classes[color]]: !hasImage && !isSearchVariant,
        [classes.withImage]: hasImage && !isSearchVariant,
      })}
      data-testid={'teaser'}
    >
      <div className={classes.textContainer}>
        <HeadlineField
          tag={convertToHeadlineTag(blok.headlineStyle, HeadlineTag.H2)}
          style={HeadlineTag.H3}
          className={classes.heading}
        >
          {headline}
        </HeadlineField>
        <RichText text={blok.text} replace={replace} />
        {blok.ctaLink && ctaLabel ? (
          <div className={classes.buttonBar}>
            <Button
              link={blok.ctaLink}
              label={ctaLabel}
              endIcon={<SvgIcon component={ArrowRight} aria-hidden={!isSearchVariant} />}
              onClick={handleCtaClick}
            />
          </div>
        ) : null}
      </div>
      {hasImage && blok.image1 && (
        <div className={classNames(classes.imageContainer)}>
          <StoryblokImage
            image={blok.image1}
            alt={blok.imageAltTag1}
            width={layout}
            badgeAIPosition={blok.alignment ? 'bottomLeft' : 'topRight'}
            maskVariant={blok.alignment ? 'mask mask-bottomLeft' : 'mask mask-topRight'}
          />
        </div>
      )}
    </div>
  );
};

export default TeaserClient;
